import { Identifier, RaRecord } from 'react-admin';

export type ShortDate = `${number}-${number}-${number}`;
export type LongDate =
  `${number}-${number}-${number}T${number}:${number}:${number}-${number}:${number}`;

export interface Line {
  item: number;
  category: number;
  price: number;
  quantity: number;
  tax: number;
  total?: number;
}

export interface Contract extends RaRecord {
  id: Identifier;
  service_id: number;
  deceased_client_id: Identifier;
  funeral_id?: Identifier;
  description: string;
  issue_date: Date;
  client_id: Identifier;
  memo: string;
  created_at: Date;
  total_amount: number;
  lines: Line[];
  is_preneed?: boolean;
  is_monthly?: boolean;
  monthly_payment_amount?: number | null;
  down_payment_amount?: number | null;
  payment_term?: number | null;
  affirm?: boolean;
  fulfillment_status?: string | null;
  fulfilled_at?: string | null;
  // Pre-need TruStage flow (T-162)
  trustage_forward?: boolean;
  trustage_status?: 'sent' | 'active' | null;
  trustage_sent_at?: string | null;
  trustage_certificate_number?: string | null;
  trustage_certificate_number_set_at?: string | null;
  trustage_certificate_number_set_by?: string | null;
  trustage_activated_at?: string | null;
  trustage_activated_by?: string | null;
  certificate_received_at?: string | null;
  certificate_document_id?: string | null;
  certificate_candidate?: {
    communication_id?: string;
    attachment_id?: string;
    filename?: string | null;
    reason?: string;
    received_at?: string;
  } | null;
  confirmation_package_scheduled_at?: string | null;
  confirmation_package_held_at?: string | null;
  confirmation_package_held_by?: string | null;
  confirmation_package_sent_at?: string | null;
  confirmation_package_shadow_at?: string | null;
}
export interface Invoice extends RaRecord {
  id: Identifier;
  status: string;
  issue_date: Date;
  due_date: Date;
  send_date: Date;
  services_total: number;
  casket_total: number;
  disburesements: number;
  balance_owed: number;
  contract_id: Identifier;
  created_at: Date;
}

export interface CreditMemo extends RaRecord {
  id: Identifier;
  contract_id: Identifier;
  created_at: Date;
}

export interface Client extends RaRecord {
  id: Identifier;
  name: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  maiden_name?: string;
  address: string;
  address2: string;
  civic_number?: string;
  apartment?: string;
  city: string;
  province: string;
  postal_code: string;
  email: string;
  birthdate: Date;
  phone_number: string;
  sex: string; //'M' | 'F';
  /** Free text, in pounds on every production row. Decides urn capacity. */
  weight?: string | null;
  hebrew_name: string;
  occupation: string;
  industry: string;
  retired: boolean;
  sin: string;
  place_of_birth: string;
  children?: ClientChild[];
  image_path?: string;
  internal_infectious_disease?: boolean;
}

export interface Category extends RaRecord {
  id: Identifier;
  name: string;
  taxable?: boolean;
}

export interface Item extends RaRecord {
  Id?: string;
  SyncToken?: string;
  Name: string;
  Description?: string;
  Active?: boolean;
  UnitPrice?: number;
  Type: 'Category' | 'Inventory' | 'Service' | 'NonInventory';
  Category: 'Caskets' | 'Service';
  IncomeAccountRef?: { value: string; name: string };
  PurchaseCost?: number;
  TrackQtyOnHand?: boolean;
  domain?: string;
  MetaData?: {
    CreateTime?: LongDate;
    LastUpdatedTime?: LongDate;
  };
}

export type DeliveryMethod =
  | 'shipping'
  | 'courier'
  | 'office_pickup'
  | 'crematorium_pickup'
  // Legacy. 'hand_delivery' reads as courier for Montreal addresses, and 'pickup'
  // was split into the two above. Nothing new should write either.
  | 'hand_delivery'
  | 'pickup';

export interface Funeral extends RaRecord {
  /** Ontario registrar filings — see OntarioPaperworkRows. */
  burial_permit_status?: 'not_requested' | 'pending' | 'issued' | 'declined' | 'failed' | null;
  burial_permit_number?: string | null;
  burial_permit_issued_at?: string | null;
  coroner_certificate_status?: 'not_requested' | 'requested' | 'received' | null;
  coroner_certificate_number?: string | null;
  coroner_certificate_received_at?: string | null;
  id: Identifier;
  uuid?: string;
  service_id: number;
  date_time_of_death: Date;
  death_after_sunset: boolean;
  funeral_date: Date;
  funeral_time: Date;
  burial_certificate_number: string;
  place_of_death: string;
  /** Staff override for the QC DEC place-of-death name (<= 35 chars). */
  dec_place_of_death_name?: string | null;
  place_of_death_type?: string;
  place_of_death_type_other?: string;
  place_of_death_code?: string;
  // jsonb: { adresse, municipalite, province, codePostal, pays }. Legacy rows
  // may still hold a plain string.
  place_of_death_address?:
    | {
        adresse?: string;
        municipalite?: string;
        province?: string;
        codePostal?: string;
        pays?: string;
      }
    | string
    | null;
  // Same jsonb shape; null means "collect at the place of death".
  pickup_address?: {
    adresse?: string;
    municipalite?: string;
    province?: string;
    codePostal?: string;
    pays?: string;
  } | null;
  death_certificate_date: Date;
  shiva_notes: string;
  shiva_start_date: Date;
  shiva_end_date: Date;
  shiva_private: boolean;
  shiva_address: string;
  shiva_phone: string;
  special_instructions: string;
  number_death_certificates: number;
  number_boxes_siddurin: number;
  deceased_client_id: Identifier;
  director_id: Identifier;
  owner_id?: Identifier;
  chapel_id: Identifier;
  primary_client_id: Identifier;
  paying_client_id?: Identifier;
  authorized_client_id?: Identifier;
  declarant_client_id?: Identifier;
  primary_client_relationship?: string;
  paying_client_relationship?: string;
  authorized_client_relationship?: string;
  declarant_client_relationship?: string;
  cemetery_id: Identifier;
  plot_location: string;
  cause_of_death_id: Identifier;
  family_room_id: Identifier;
  number_sons: number;
  number_daughters: number;
  cremation_status?: string;
  transport_partner_id?: string | Identifier;
  crematorium_partner_id?: string | Identifier;
  /** T-192: per-case ships-from site (Milton remains transferred to Brampton or Ajax). */
  ships_from_partner_id?: string | Identifier | null;
  /** Where the body is dropped off for storage/prep. NULL = same as the crematorium. */
  dropoff_partner_id?: string | Identifier | null;
  /** When the drop-off facility released the body to the crematorium. */
  dropoff_released_at?: string | null;
  /** Who recorded the release — partner facility name, or the staff member. */
  dropoff_released_by?: string | null;
  sp3_received?: boolean;
  sp3_number?: string | null;
  /** SIED process step, from funerals_searchable (sied_records). See funerals/siedStatus.ts. */
  sied_status?: string | null;
  sied_status_raw?: string | null;
  sied_status_checked_at?: string | null;
  sied_status_changed_at?: string | null;
  contract_signed?: boolean;
  cremation_agreement_signed?: boolean;
  paperwork1_complete?: boolean;
  payment_complete?: boolean;
  status_history?: TimelineEvent[];
  language: 'en' | 'fr';
  region?: 'ontario' | 'quebec';
  /** Override of which benefits program the case applies for; NULL = region default. */
  benefits_program?: 'cpp' | 'qpp' | null;
  sp3_data?: Record<string, any> | null;
  cremation_date?: Date;
  delivery_date?: Date;
  delivery_day?: string;
  delivery_window?: string;
  delivery_method?: DeliveryMethod | null;
  delivery_address?: DeliveryAddress;
  delivery_note?: string;
  split_ashes?: 'no-split' | 'split-two' | 'split-three' | 'split-other' | null;
  urn_type?: 'cardboard' | 'custom' | null;
  /** Ashes-handling instruction shown to the crematorium on the partner portal. Max 200 chars (DB-enforced). */
  crematorium_special_instructions?: string | null;
  shipping_label_url?: string;
  shipping_tracking_number?: string;
  /** The separately shipped urn's own tracking number, from the supplier. */
  urn_tracking_number?: string | null;
  /** Carrier for urn_tracking_number — detected from the number, editable. */
  urn_tracking_carrier?: string | null;
  /** The urn tracking number the family was texted about; never texted twice. */
  urn_notify_sent_for?: string | null;
  urn_notify_sent_at?: string | null;
  /** Supersedes urn_type, which conflated a purchase with a family-supplied urn. */
  urn_choice?: 'cardboard' | 'cleo' | 'family_own' | null;
  /** Which Cleo display urn. Null while unknown — the form did not ask before 2026-09. */
  urn_colour?: 'blue' | 'beige' | null;
  /** Where the supplier order has got to, for a Cleo urn. */
  urn_order_status?: 'to_order' | 'ordered' | 'shipped' | 'received' | null;
  shipping_shipment_id?: string;
  shipping_status?: string | null;
  /** Staff queued the tracking text to go out when Canada Post collects the parcel. */
  shipping_notify_on_ship?: boolean;
  /** The message staff approved, sent verbatim when the parcel is scanned. */
  shipping_notify_body?: string | null;
  /** Set when the queued text went out; stops it repeating on later scans. */
  shipping_notify_sent_at?: string | null;
  shipping_notify_emailed_pin?: string | null;
  courier_call_number?: string | null;
  courier_status?: string | null;
  courier_scheduled_at?: string | null;
  /** The single definition of "delivery confirmed" — never re-infer it from delivery_day. */
  delivery_confirmed_at?: string | null;
  delivery_confirmed_by?: 'family' | 'staff' | null;
  /** Set when staff move the case to ready_for_delivery and decline to message the family. */
  delivery_invite_suppressed_at?: string | null;
  /** When the self-scheduling link went out. Also starts the agent's grace period. */
  delivery_invite_sent_at?: string | null;
  delivery_reminder_sent_at?: string | null;
  /** Where the family collects: our office, or a Southwest location. Pickup methods only. */
  pickup_location_id?: string | null;
  /** When the ashes move to the pickup location — the office courier leg. */
  transfer_day?: string | null;
  death_benefits_status?: DeathBenefitsStatus;
  benefits_confirmation_number?: string | null;
  benefits_ineligible_reason?: string | null;
  death_certificate_number?: string | null;
  contracts_sent_at?: string | null;
  contracts_signed_at?: string | null;
  /** Maintained by the contracts trigger (update_funeral_contract_flags). */
  has_preneed?: boolean | null;
  has_atneed?: boolean | null;
  /** 'preplan' = pre-plan only, no contract or payment expected (T-172). */
  preneed_mode?: 'prepaid' | 'preplan' | null;
  preneed_mode_changed_at?: string | null;
  preneed_mode_changed_by?: string | null;
  contracts_signed_urls?: string[] | null;
  cpp_benefits_signed_at?: string | null;
  cpp_benefits_signed_urls?: string[] | null;
  cpp_benefits_mailed_at?: string | null;
  cpp_benefits_mailed_to?: any;
  cpp_benefits_mailing_status?: 'pending' | 'sent' | 'failed' | null;
  paperwork2_complete?: boolean;
  dec_ready?: boolean;
  visible_to_partners?: boolean;
  /** Contract carries a witness of cremation (QuickBooks item 19 QC / 32 ON). */
  has_visitation?: boolean;
  /** Contract carries Private Identification ON (QuickBooks item 33). */
  has_private_identification?: boolean;
  /** The witness time Cleo scheduled. */
  visitation_datetime?: string | null;
  /** Legacy: the witness time the facility confirmed from the portal. Retired (DROPOFF_LEDGER_PLAN Q5); shown read-only if set. */
  witness_confirmed_at?: string | null;
  witness_confirmed_by?: string | null;
  /** Set when the drop-off facility removes an implanted device. */
  pacemaker_removed_at?: string | null;
  pacemaker_removed_by?: string | null;
  /** Chargeable storage days claimed by the drop-off facility (starts at 48h). */
  dropoff_storage_days?: number | null;
  dropoff_storage_days_at?: string | null;
  dropoff_storage_days_by?: string | null;
  // Drop-off ledger (DROPOFF_LEDGER_PLAN §2) — recorded by the facility in the
  // partner portal; read-only in admin except private_identification_at.
  /** When the body arrived at the drop-off facility. Day 0 of "days in care". */
  dropoff_received_at?: string | null;
  /** Facility staff member who received the body (free text). */
  dropoff_received_by?: string | null;
  /** Pacemaker / implant found at intake. Removal is pacemaker_removed_at. */
  dropoff_implant?: 'no' | 'yes' | 'unknown' | null;
  /** Confirmed time of the Ontario private visitation at the drop-off facility, set by Cleo. */
  private_identification_at?: string | null;
  /** Personal belongings that came in with the body (free text). */
  dropoff_belongings?: string | null;
  /** Set when the facility ticks "Mailed back". */
  dropoff_belongings_mailed_at?: string | null;
  /** T-099: how the belongings go home. Set by Cleo here; the portal shows it. */
  dropoff_belongings_method?: 'mail' | 'pickup' | null;
  /** Who they go to (mail) or who collects them (pickup), snapshot when chosen. */
  dropoff_belongings_contact?: BelongingsContact | null;
  /** Belongings Canada Post label, separate from the remains' shipping_* columns. */
  dropoff_belongings_label_url?: string | null;
  dropoff_belongings_tracking_number?: string | null;
  dropoff_belongings_shipment_id?: string | null;
  dropoff_belongings_label_created_at?: string | null;
  dropoff_notes?: string | null;
  causes_of_death_text?: string | null;
  delivered_at?: string | null;
  aftercare_email_sent_at?: string | null;
  aftercare_email_scheduled_date?: string | null;
  aftercare_email_enabled?: boolean | null;
  aftercare_email_sent?: boolean;
  aftercare_call_1_status?: AftercareCall1Status;
  /** Derived in funerals_searchable from the customer_events log. */
  paperwork_complete_email_sent_at?: string | null;
  /** Set by "Close without email" — see funerals/caseClosure.ts. */
  paperwork_email_waived_at?: string | null;
  paperwork_email_waived_by?: string | null;
  paperwork_email_waived_reason?: string | null;
}

export interface Aftercare {
  id: number;
  funeral_id: number;
  email_enabled: boolean;
  email_scheduled_date?: string | null;
  email_sent_at?: string | null;
  email_message?: string | null;
  email_recipient_client_id?: number | null;
  call_1_status?: AftercareCall1Status;
  call_1_completed_at?: string | null;
  call_1_completed_by_user_id?: number | null;
  call_1_completed_by_user_name?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: number | null;
  created_by_user_name?: string | null;
}

/** funerals.dropoff_belongings_contact (T-099). client_id null = typed by hand. */
export interface BelongingsContact {
  client_id?: number | null;
  name?: string | null;
  phone?: string | null;
  civic_number?: string | null;
  address?: string | null;
  apartment?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
}

export interface DeliveryAddress {
  selection?: string;
  address?: string;
  apartment?: string;
  civic_number?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  recipient_name?: string;
  recipient_phone?: string;
  // Legacy Google Places API fields
  street_number?: string;
  route?: string;
  locality?: string;
  administrative_area_level_1?: string;
  country?: string;
  formatted_address?: string;
}

export interface Director extends RaRecord {
  id: Identifier;
  name: string;
}

export interface Synagogue extends RaRecord {
  id: Identifier;
  name: string;
}

export interface Attachments extends RaRecord {
  id: Identifier;
  title: string;
  path: string;
  funeral_id: number;
  document_type?: string;
  url?: string;
}

export type QbInvoice = {
  id: string;
  Balance: number;
  Lines: QuickbooksItemLine[];
  CustomField: [
    {
      DefinitionId: '1';
      StringValue: string;
      Type: 'StringType';
      Name: 'Service ID';
    }
  ];
};
export type QuickbooksItemLine = {
  Id?: string;
  Amount: number;
  DetailType: 'SalesItemLineDetail';
  Description: string;
  SalesItemLineDetail: {
    id?: string;
    ServiceDate?: ShortDate;
    ItemRef?: {
      value: string;
      name?: string;
    };
    UnitPrice: number;
    Qty: number;
    TaxCodeRef: {
      value: string;
    };
  };
};
export interface Chapel extends RaRecord {
  id: Identifier;
  name: string;
}

interface ClientChild {
  firstName: string;
  lastName: string;
  birthdate: string;
  isOver18: boolean;
}

export enum CremationStatus {
  ARRANGEMENT = 'arrangement',
  TRANSPORT_DISPATCHED = 'transport_dispatched',
  CREMATION_SCHEDULED = 'cremation_scheduled',
  READY_FOR_DELIVERY = 'ready_for_delivery',
  DELIVERY_SCHEDULED = 'delivery_scheduled',
  DELIVERED = 'delivered'
}

export enum AftercareCall1Status {
  PENDING = 'pending',
  COMPLETED = 'completed',
  DECLINED = 'declined',
}

export enum DeathBenefitsStatus {
  NOT_COMPLETED = 'not_completed',
  COMPLETED = 'completed',
  INELIGIBLE = 'ineligible'
}

export enum CustomerEventType {
  PHONE_CALL = 'phone_call',
  TEXT_MESSAGE = 'text_message',
  EMAIL = 'email',
  STATUS_CHANGE = 'status_change',
  CUSTOMER_MESSAGE = 'customer_message',
  CPP_BENEFITS_SIGNED = 'cpp_benefits_signed',
  CPP_BENEFITS_MAILED = 'cpp_benefits_mailed',
  MAIL_SENT = 'mail_sent',
  CONTRACT_SENT = 'contract_sent',
  CONTRACT_OPENED = 'contract_opened',
  CONTRACT_SIGNED = 'contract_signed',
  CONTRACT_PAID = 'contract_paid',
  PAYMENT_RECEIPT_SENT = 'payment_receipt_sent',
  REVIEW_REQUEST_SENT = 'review_request_sent',
  INTERNAL_NOTE = 'internal_note',
  ATTESTATION_SENT = 'attestation_sent',
  PREP_SHEET_SENT = 'prep_sheet_sent',
  THANK_YOU_CARD_REQUESTED = 'thank_you_card_requested',
  THANK_YOU_CARD_SENT = 'thank_you_card_sent',
  PAGE_VIEW = 'page_view',
  AFTERCARE_EMAIL_SENT = 'aftercare_email_sent',
  PAPERWORK_COMPLETE_EMAIL_SENT = 'paperwork_complete_email_sent',
  // T-100: one entry per Sign in SIED attempt (message + outcome in status).
  SIED_SIGN = 'sied_sign',
  // Agent set the benefits packet to a manual terminal state. `status` carries
  // the state ('ineligible' | 'completed'), `message` the reason / confirmation #.
  BENEFITS_STATUS_CHANGE = 'benefits_status_change',
  // Pre-need TruStage flow (T-162). `message` carries the full sentence.
  PRENEED_TRUSTAGE_NOT_SENT = 'preneed_trustage_not_sent',
  PRENEED_CERTIFICATE_RECEIVED = 'preneed_certificate_received',
  PRENEED_CERTIFICATE_TO_CONFIRM = 'preneed_certificate_to_confirm',
  PRENEED_CERTIFICATE_DISMISSED = 'preneed_certificate_dismissed',
  PRENEED_PACKAGE_SCHEDULED = 'preneed_package_scheduled',
  PRENEED_PACKAGE_NOT_SCHEDULED = 'preneed_package_not_scheduled',
  PRENEED_PACKAGE_HELD = 'preneed_package_held',
  PRENEED_PACKAGE_SHADOW = 'preneed_package_shadow',
  PRENEED_PACKAGE_SENT = 'preneed_package_sent',
  // Manual TruStage actions (T-212).
  PRENEED_POLICY_NUMBER_SET = 'preneed_policy_number_set',
  PRENEED_MARKED_ACTIVE = 'preneed_marked_active',
  PRENEED_ACTIVE_UNDONE = 'preneed_active_undone',
  // TruStage claim at fruition (T-174). `message` carries the full sentence.
  PRENEED_CLAIM_APPROVED = 'preneed_claim_approved',
  PRENEED_CLAIM_FORM_SENT = 'preneed_claim_form_sent',
  PRENEED_CLAIM_SIGNED = 'preneed_claim_signed',
  PRENEED_CLAIM_SENT = 'preneed_claim_sent',
  PRENEED_CLAIM_SEND_FAILED = 'preneed_claim_send_failed',
  PRENEED_CLAIM_PAID = 'preneed_claim_paid',
  PRENEED_CLAIM_OVERDUE = 'preneed_claim_overdue',
}

export interface CustomerEvent {
  id?: number | string;
  funeral_id: number;
  timestamp: string;
  type: CustomerEventType;
  status?: CremationStatus;
  recipient_type?: 'transport' | 'dropoff' | 'crematorium' | 'customer';
  recipient_id?: number;
  recipient_name?: string;
  contact_method?: string; // e.g., "phone", "email", "sms"
  contact_detail?: string; // e.g., phone number or email
  subject?: string;
  message?: string;
  user_id?: number;
  user_name?: string;
  source_id?: string; // call_sessions.id for phone calls
  metadata?: Record<string, any>; // communications.metadata (SendGrid status, timestamps, etc.)
  aircall_message_id?: string; // outbound SMS — keys realtime delivery-status updates
  direction?: 'inbound' | 'outbound'; // communications direction (email/SMS); null on system events
  gmail_thread_id?: string; // Gmail thread id for email grouping
  body_html?: string | null; // emails with no plain body: what the viewers render instead
  call_status?: 'answered' | 'missed' | 'voicemail' | null; // communications.call_status; null on non-calls
  duration_seconds?: number | null; // calls only — Aircall counts ringing, so not proof of an answer
}

export interface TimelineEvent {
  status?: CremationStatus;
  timestamp: string;
  user?: string;
  notes?: string;
  communicationSent?: boolean;
  type?: CustomerEventType;
  recipient_name?: string;
  contact_method?: string;
  contact_detail?: string;
  subject?: string;
  message?: string;
}

// Component-specific interface for the internal status timeline
export interface StatusTimelineEvent {
  status: CremationStatus;
  timestamp: string;
  user?: string;
  notes?: string;
  communicationSent?: boolean;
  type?: CustomerEventType;
  recipient_name?: string;
  contact_method?: string;
  contact_detail?: string;
  subject?: string;
  message?: string;
}

export interface Partner extends RaRecord {
  id: Identifier;
  name: string;
  /** Role capabilities — a partner can hold several at once (e.g. a transport company whose facility is also a drop-off location). */
  is_transport?: boolean;
  is_crematorium?: boolean;
  is_dropoff_location?: boolean;
  /** Families may collect ashes here. Staff-set only — pickup is never offered on the customer site. */
  is_ashes_pickup_location?: boolean;
  /** Shown to the family on the delivery page and in the morning-of message. */
  pickup_hours?: string | null;
  is_answering_service?: boolean;
  /** Market the partner serves; funeral dropdowns filter to the case's region. NULL = shown everywhere. */
  region?: 'ontario' | 'quebec' | null;
  phone: string;
  email: string;
  address: string;
  city: string;
  province: string;
  postal_code: string;
  notes: string;
  phones?: PhoneContact[];
  prep_sheet_emails?: string | null;
  /** Whether this partner's portal page is gated on a PIN at all. */
  require_pin?: boolean;
  /** This partner's own 4-digit portal PIN. NULL = fall back to the shared PARTNER_ACCESS_PIN. */
  access_pin?: string | null;
  /** Which cremation agreement this crematorium uses; drives the DocuSeal template. NULL = region default. */
  agreement_type?: 'rideau' | 'cleo' | 'southwest' | null;
  /** Default crematorium for its agreement when several share it (Southwest Brampton). One per agreement. */
  is_default?: boolean;
  /** Whether this crematorium can take cases that include visitation. Cardinal cannot. */
  accepts_visitation?: boolean;
  /** Weekdays Canada Post collects here (0=Sun…6=Sat). Sets the automatic ship day (T-103). */
  canada_post_pickup_weekdays?: number[] | null;
}

export interface PhoneContact {
  label: string;
  number: string;
}

export type ReviewRequestSendMethod = 'sms' | 'email' | 'both';

export interface ReviewRequest {
  id?: number;
  funeral_id: number;
  enabled: boolean;
  send_method: ReviewRequestSendMethod;
  recipient_client_id: number;
  message: string;
  scheduled_date: string;
  sent_at?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: number;
  created_by_user_name?: string;
}
